<template>
  <div ref="noticeWrapper">
    <a-popover
      v-model="visible"
      trigger="click"
      @visibleChange="handleVisibleChange"
      placement="bottomLeft"
      overlayClassName="header-notice-wrapper"
      :getPopupContainer="() => $refs.noticeWrapper"
      :autoAdjustOverflow="true"
      :arrowPointAtCenter="true"
      :overlayStyle="{ width: '300px', top: '50px' }"
    >
      <template slot="content">
        <a-spin :spinning="loading">
          <a-tabs v-model="queryParam.noticeType" @change="changeType">
            <a-tab-pane v-for="d in displayTabOptions" :key="d.dictValue">
              <span slot="tab">
                <a-badge :count="tabCountMap[d.dictValue] || 0" :show-zero="false" :overflow-count="99">
                  <span class="header-notice-tab">{{ d.dictLabel }}</span>
                </a-badge>
              </span>
              <a-list style="max-height: 300px; overflow:auto;cursor: pointer;">
                <div
                  v-if="showLoadingMore"
                  slot="loadMore"
                  :style="{ textAlign: 'center', marginTop: '12px', height: '32px', lineHeight: '32px' }"
                >
                  <a-spin v-if="loadingMore && !isTodoTab" />
                  <a-button v-else @click="handleLoadMore">
                    查看更多
                  </a-button>
                </div>
                <a-list-item v-for="(item, index) in list" :key="isTodoTab ? (item.id || index) : index">
                  <a-list-item-meta v-if="isTodoTab">
                    <a slot="title" class="header-notice-todo-link" href="javascript:;" @click.prevent="onTodoClick(item)">
                      <ellipsis :length="32" tooltip>{{ item.title }}</ellipsis>
                    </a>
                    <div slot="description" class="header-notice-todo-meta">
                      <span class="header-notice-todo-time">{{ parseTime(item.createTime) }}</span>
                      <span class="header-notice-todo-tags">
                        <a-tag v-if="item.bizTypeName || item.bizType" color="blue" size="small">
                          {{ item.bizTypeName || item.bizType }}
                        </a-tag>
                        <a-tag :color="priorityTagColor(item.priority)" size="small">
                          {{ priorityText(item.priority) }}
                        </a-tag>
                      </span>
                    </div>
                  </a-list-item-meta>
                  <a-list-item-meta v-else :description="item.createTime">
                    <a slot="title" href="javascript:;" @click.prevent="openSystemNotice(item)">
                      <ellipsis :length="32" tooltip>{{ item.noticeTitle }}</ellipsis>
                    </a>
                  </a-list-item-meta>
                </a-list-item>
              </a-list>
            </a-tab-pane>
          </a-tabs>
        </a-spin>
      </template>
      <span ref="noticeRef" class="header-notice-trigger">
        <a-badge :count="unreadCount" :overflow-count="99" :offset="[-2, 4]">
          <span class="header-notice">
            <a-icon style="font-size: 20px;" type="bell" />
          </span>
        </a-badge>
      </span>
    </a-popover>
    <notice-detail ref="noticeDetail" :typeOptions="typeOptions" />
  </div>
</template>

<script>
import { pageNotice } from '@/api/system/notice'
import { pageAnnouncement } from '@/api/diandian/announcement'
import { getInfo } from '@/api/login'
import auth from '@/plugins/auth'
import Ellipsis from '@/components/Ellipsis'
import NoticeDetail from './NoticeDetail'

const TODO_TAB_VALUE = '__announcement_todo__'

const BIZ_ANNOUNCEMENT_LIST_PATH = '/announcement/bizAnnouncement'

export default {
  name: 'HeaderNotice',
  components: {
    Ellipsis,
    NoticeDetail
  },
  watch: {
    '$route.fullPath' () {
      this.fetchUnreadCount()
      if (this.visible) {
        this.refreshNoticeList()
      }
    }
  },
  computed: {
    canViewSystemNotice () {
      return auth.hasPermi('system:notice:list')
    },
    showTodoTab () {
      return auth.hasPermi('diandian:announcement:list')
    },
    displayTabOptions () {
      const todo = { dictValue: TODO_TAB_VALUE, dictLabel: '待办' }
      const base = this.canViewSystemNotice ? (this.typeOptions || []) : []
      if (this.showTodoTab) {
        return [todo].concat(base)
      }
      return base
    },
    isTodoTab () {
      return this.queryParam.noticeType === TODO_TAB_VALUE
    }
  },
  data () {
    return {
      loading: false,
      loadingMore: false,
      showLoadingMore: true,
      visible: false,
      ws: null,
      reconnectTimer: null,
      initSocketTimer: null,
      reconnectDelay: 3000,
      userId: null,
      unreadCount: 0,
      total: 0,
      tabCountMap: {},
      queryParam: {
        pageNum: 1,
        pageSize: 5,
        status: 0,
        noticeType: '1'
      },
      list: [],
      typeOptions: []
    }
  },
  created () {
    this.ensureTypeOptions().then(() => {
      const tabs = this.displayTabOptions
      if (tabs.length) {
        const first = tabs[0].dictValue
        if (first === TODO_TAB_VALUE) {
          this.queryParam = { pageNum: 1, pageSize: 5, noticeType: TODO_TAB_VALUE, handleStatus: 0 }
        } else {
          this.queryParam = { pageNum: 1, pageSize: 5, status: 0, noticeType: first }
        }
      }
      this.fetchUnreadCount()
    })
    this.initNoticeSocket()
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', this.handleWindowFocus)
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange)
    }
  },
  beforeDestroy () {
    this.clearReconnectTimer()
    this.clearInitSocketTimer()
    this.closeSocket()
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', this.handleWindowFocus)
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    }
  },
  methods: {
    extractPageRecords (response) {
      if (response && response.data) {
        if (Array.isArray(response.data.records)) {
          return response.data.records
        }
        if (Array.isArray(response.data)) {
          return response.data
        }
      }
      return []
    },
    extractPageTotal (response) {
      if (response && response.data) {
        const total = Number(response.data.total)
        if (!isNaN(total)) {
          return total
        }
        if (Array.isArray(response.data.records)) {
          return response.data.records.length
        }
        if (Array.isArray(response.data)) {
          return response.data.length
        }
      }
      return 0
    },
    ensureTypeOptions () {
      if (!this.canViewSystemNotice) {
        this.typeOptions = []
        return Promise.resolve(this.typeOptions)
      }
      if (this.typeOptions.length > 0) {
        return Promise.resolve(this.typeOptions)
      }
      return this.getDicts('sys_notice_type').then(response => {
        this.typeOptions = response.data || []
        return this.typeOptions
      })
    },
    getList () {
      this.loading = true
      const req = this.isTodoTab
        ? pageAnnouncement({
            pageNum: this.queryParam.pageNum,
            pageSize: this.queryParam.pageSize,
            handleStatus: 0
          })
        : pageNotice(this.queryParam)
      req.then(response => {
          const records = this.extractPageRecords(response)
          this.list = this.queryParam.pageNum > 1 ? this.list.concat(records) : records
          this.total = this.extractPageTotal(response)
          if (this.total <= this.queryParam.pageNum * this.queryParam.pageSize) {
            this.showLoadingMore = false
          } else {
            this.showLoadingMore = true
          }
          this.loading = false
          this.loadingMore = false
        }
      ).catch(() => {
        this.loading = false
        this.loadingMore = false
      }
      )
    },
    fetchUnreadCount () {
      return this.ensureTypeOptions().then(typeOptions => {
        const tabs = this.displayTabOptions || []
        const requests = tabs.map(item => {
          if (item.dictValue === TODO_TAB_VALUE) {
            return pageAnnouncement({
              pageNum: 1,
              pageSize: 1,
              handleStatus: 0
            })
          }
          return pageNotice({
            pageNum: 1,
            pageSize: 1,
            status: 0,
            noticeType: item.dictValue
          })
        })
        if (!requests.length) {
          this.unreadCount = 0
          this.tabCountMap = {}
          return 0
        }
        return Promise.all(requests).then(responses => {
          const tabCountMap = {}
          let unreadCount = 0
          responses.forEach((response, index) => {
            const tab = tabs[index]
            const count = this.extractPageTotal(response)
            if (tab) {
              tabCountMap[tab.dictValue] = count
            }
            unreadCount += count
          })
          this.tabCountMap = tabCountMap
          this.unreadCount = unreadCount
          return this.unreadCount
        })
      }).catch(() => {
        this.unreadCount = 0
        this.tabCountMap = {}
        return 0
      })
    },
    async initNoticeSocket () {
      try {
        const res = await getInfo()
        const user = (res && res.data) || {}
        this.userId = user.userId || user.id || null
        if (!this.userId) {
          this.scheduleInitNoticeSocket()
          return
        }
        this.connectSocket()
      } catch (e) {
        console.warn('获取当前登录用户信息失败，无法建立通知 WebSocket 连接', e)
        this.scheduleInitNoticeSocket()
      }
    },
    scheduleInitNoticeSocket () {
      this.clearInitSocketTimer()
      this.initSocketTimer = setTimeout(() => {
        this.initNoticeSocket()
      }, this.reconnectDelay)
    },
    connectSocket () {
      if (!this.userId || typeof WebSocket === 'undefined') {
        return
      }
      if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
        return
      }
      const socketUrl = this.buildSocketUrl()
      if (!socketUrl) {
        return
      }
      this.ws = new WebSocket(socketUrl)
      this.ws.onopen = () => {
        this.clearReconnectTimer()
        this.clearInitSocketTimer()
        this.fetchUnreadCount()
        if (this.visible) {
          this.refreshNoticeList()
        }
      }
      this.ws.onmessage = () => {
        this.refreshNoticeList()
        this.fetchUnreadCount()
      }
      this.ws.onerror = () => {
        this.scheduleReconnect()
      }
      this.ws.onclose = () => {
        this.scheduleReconnect()
      }
    },
    buildSocketUrl () {
      try {
        let rawBaseApi = (process.env.VUE_APP_BASE_API || '').trim().replace(/^['"]|['"]$/g, '')
        // 常见误配：https://wss://host/... 会被 URL 解析成 host=wss，拼出 ws://wss://...
        rawBaseApi = rawBaseApi.replace(/^https?:\/\/wss:\/\//i, 'wss://')
        rawBaseApi = rawBaseApi.replace(/^https?:\/\/ws:\/\//i, 'ws://')

        let baseWsUrl = ''
        if (/^wss?:\/\//i.test(rawBaseApi)) {
          const u = new URL(rawBaseApi)
          const path = (u.pathname || '').replace(/\/$/, '')
          baseWsUrl = `${u.protocol}//${u.host}${path}`
        } else if (/^https?:\/\//i.test(rawBaseApi)) {
          const u = new URL(rawBaseApi)
          const wsProtocol = u.protocol === 'https:' ? 'wss:' : 'ws:'
          const path = (u.pathname || '').replace(/\/$/, '')
          baseWsUrl = `${wsProtocol}//${u.host}${path}`
        } else {
          const u = new URL(rawBaseApi || '/', window.location.origin)
          const wsProtocol = u.protocol === 'https:' ? 'wss:' : 'ws:'
          const path = (u.pathname || '').replace(/\/$/, '')
          baseWsUrl = `${wsProtocol}//${u.host}${path}`
        }

        let socketUrl = `${baseWsUrl}/webSocket/${this.userId}`
        // 兜底：若仍出现双协议，只保留 wss:// 或 ws://
        socketUrl = socketUrl.replace(/^ws:\/\/wss:\/\//i, 'wss://')
        socketUrl = socketUrl.replace(/^wss:\/\/wss:\/\//i, 'wss://')
        socketUrl = socketUrl.replace(/^ws:\/\/ws:\/\//i, 'ws://')
        return socketUrl
      } catch (e) {
        console.warn('构建通知 WebSocket 地址失败', e)
        return ''
      }
    },
    scheduleReconnect () {
      this.clearReconnectTimer()
      this.reconnectTimer = setTimeout(() => {
        this.connectSocket()
      }, this.reconnectDelay)
    },
    clearReconnectTimer () {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
    },
    clearInitSocketTimer () {
      if (this.initSocketTimer) {
        clearTimeout(this.initSocketTimer)
        this.initSocketTimer = null
      }
    },
    closeSocket () {
      if (this.ws) {
        this.ws.onerror = null
        this.ws.onclose = null
        this.ws.close()
        this.ws = null
      }
    },
    handleWindowFocus () {
      this.fetchUnreadCount()
      if (this.visible) {
        this.refreshNoticeList()
      }
    },
    handleVisibilityChange () {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        this.handleWindowFocus()
      }
    },
    refreshNoticeList () {
      this.queryParam.pageNum = 1
      this.list = []
      this.getList()
    },
    fetchNotice () {
      this.resetQuery()
      this.ensureTypeOptions().then(() => {
        this.getList()
        this.fetchUnreadCount()
      })
    },
    handleVisibleChange (visible) {
      this.visible = visible
      if (visible) {
        this.fetchNotice()
      }
    },
    resetQuery (noticeType) {
      const tabs = this.displayTabOptions
      const fallback = tabs.length ? tabs[0].dictValue : '1'
      const nt = noticeType != null ? noticeType : fallback
      if (nt === TODO_TAB_VALUE) {
        this.queryParam = {
          pageNum: 1,
          pageSize: 3,
          noticeType: TODO_TAB_VALUE,
          handleStatus: 0
        }
      } else {
        this.queryParam = {
          pageNum: 1,
          pageSize: 5,
          status: 0,
          noticeType: nt
        }
      }
      this.list = []
    },
    changeType (key) {
      this.resetQuery(key)
      this.getList()
    },
    handleLoadMore () {
      if (this.isTodoTab) {
        this.visible = false
        this.$router.push({ path: BIZ_ANNOUNCEMENT_LIST_PATH })
        return
      }
      this.loadingMore = true
      this.queryParam.pageNum++
      this.getList()
    },
    priorityText (priority) {
      if (priority === 1) return '重要'
      if (priority === 2) return '紧急'
      return '普通'
    },
    priorityTagColor (priority) {
      if (priority === 1) return 'orange'
      if (priority === 2) return 'red'
      return 'default'
    },
    openSystemNotice (item) {
      if (!item) {
        return
      }
      const id = item.id != null ? item.id : item.noticeId
      if (id == null || id === '') {
        this.$message.warning('无法打开通知：缺少公告编号')
        return
      }
      if (!this.$refs.noticeDetail) {
        return
      }
      this.$refs.noticeDetail.openNotice(id)
    },
    onTodoClick (item) {
      if (!item || !item.id) return
      this.visible = false
      this.$router.push({
        path: BIZ_ANNOUNCEMENT_LIST_PATH,
        query: {
          handleStatus: '0',
          announcementId: String(item.id)
        }
      })
      this.refreshNoticeList()
      this.fetchUnreadCount()
    }
  }
}
</script>

<style lang="css">
  .header-notice-wrapper {
    top: 50px !important;
  }
</style>
<style lang="less" scoped>
  .header-notice-trigger {
    display: inline-block;
  }
  .header-notice{
    display: inline-block;
    transition: all 0.3s;

    span {
      vertical-align: initial;
    }
  }
  .header-notice-tab {
    display: inline-block;
    padding-right: 6px;
  }
  .header-notice-todo-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 4px;
  }
  .header-notice-todo-time {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
    line-height: 22px;
    flex-shrink: 0;
  }
  .header-notice-todo-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    justify-content: flex-end;
    flex: 1;
    min-width: 0;
  }
  .header-notice-todo-link {
    display: inline-block;
    color: rgba(0, 0, 0, 0.85);
  }
  .header-notice-todo-link:hover {
    color: #1890ff;
  }
</style>
